import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  ArrowLeft,
  ArrowUpRight,
  Filter,
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  Sparkles,
  FileText,
  CheckCircle2,
  CircleDot,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "fk_admin_token";

const STATUS_LABEL = {
  new: "Neu",
  in_progress: "In Bearbeitung",
  done: "Erledigt",
  archived: "Archiviert",
};

const STATUS_OPTIONS = ["new", "in_progress", "done", "archived"];

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login fehlgeschlagen.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-[#121214] border border-white/10 p-10"
      >
        <div className="overline mb-3">— Fritz Kamm AG · Admin</div>
        <h1 className="font-display text-3xl tracking-tight mb-8">
          Anmelden
        </h1>
        <label className="overline block mb-2">Passwort</label>
        <input
          type="password"
          data-testid="admin-password"
          className="field mb-8"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••"
          autoFocus
          required
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-4 text-sm font-semibold hover:bg-zinc-200 transition-colors disabled:opacity-60"
        >
          {submitting ? "Wird geprüft…" : "Anmelden"}
          <ArrowUpRight className="w-4 h-4" />
        </button>
        <a
          href="/"
          className="mt-6 inline-flex items-center gap-2 text-[12px] text-zinc-500 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Zur Website
        </a>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ source: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.source) params.set("source", filter.source);
      if (filter.status) params.set("status", filter.status);
      const [list, st] = await Promise.all([
        axios.get(`${API}/admin/consultations?${params.toString()}`, auth),
        axios.get(`${API}/admin/consultations/stats`, auth),
      ]);
      setItems(list.data);
      setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Sitzung abgelaufen. Bitte erneut anmelden.");
      } else {
        toast.error(err?.response?.data?.detail || "Laden fehlgeschlagen.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter.source, filter.status]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/consultations/${id}`, { status }, auth);
      toast.success(`Status: ${STATUS_LABEL[status]}`);
      fetchAll();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktualisieren fehlgeschlagen.");
    }
  };

  const downloadAttachment = (id, filename) => {
    const url = `${API}/admin/consultations/${id}/attachment`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("download failed");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename || "anhang";
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Download fehlgeschlagen."));
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginScreen onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white">
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#141416",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 0,
            fontFamily: "Inter, sans-serif",
          },
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 border border-white/30 flex items-center justify-center font-display font-semibold text-[13px] tracking-tighter">
              FK
            </div>
            <span className="font-display text-[14px] font-semibold tracking-tight text-white whitespace-nowrap">
              Fritz Kamm AG <span className="text-zinc-500 font-normal">· Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/admin/preise"
              data-testid="admin-pricing-link"
              className="inline-flex items-center gap-2 border border-white/15 hover:bg-white/5 px-3 py-2 text-[12px] uppercase tracking-[0.18em]"
            >
              Preise verwalten
            </a>
            <button
              onClick={fetchAll}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-white/15 hover:bg-white/5 px-3 py-2 text-[12px] uppercase tracking-[0.18em]"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Aktualisieren
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white"
            >
              <LogOut className="w-3.5 h-3.5" />
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <section data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          {[
            ["Gesamt", stats?.total ?? "–"],
            ["Neu", stats?.new ?? "–"],
            ["Verfahren-Finder", stats?.verfahren_finder ?? "–"],
            ["Offerte-Formular", stats?.contact_form ?? "–"],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#0E0E10] p-6 md:p-7">
              <div className="overline mb-2">{k}</div>
              <div className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                {v}
              </div>
            </div>
          ))}
        </section>

        {/* Filters */}
        <section className="mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-zinc-500">
            <Filter className="w-3.5 h-3.5" />
            Filter:
          </div>
          <select
            data-testid="admin-filter-source"
            value={filter.source}
            onChange={(e) => setFilter({ ...filter, source: e.target.value })}
            className="bg-[#121214] border border-white/15 text-white text-sm px-3 py-2 cursor-pointer"
          >
            <option value="">Alle Quellen</option>
            <option value="verfahren_finder">Nur Verfahren-Finder</option>
            <option value="contact_form">Nur Offerte-Formular</option>
          </select>
          <select
            data-testid="admin-filter-status"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="bg-[#121214] border border-white/15 text-white text-sm px-3 py-2 cursor-pointer"
          >
            <option value="">Alle Status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
          <div className="ml-auto text-[12px] text-zinc-500">
            {items.length} Ergebnisse
          </div>
        </section>

        {/* List */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
          <div className="lg:col-span-7 bg-[#0E0E10]">
            <div className="divide-y divide-white/10" data-testid="admin-list">
              {items.length === 0 && !loading && (
                <div className="p-10 text-center text-zinc-500">Keine Anfragen.</div>
              )}
              {items.map((it) => {
                const isFinder = it.source === "verfahren_finder";
                const isActive = selected?.id === it.id;
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => setSelected(it)}
                    data-testid={`admin-row-${it.id}`}
                    className={`relative w-full text-left p-5 md:p-6 hover:bg-[#161618] transition-colors flex items-start gap-4 ${
                      isActive ? "bg-[#161618]" : ""
                    } ${isFinder ? "bg-[#10100E]" : ""}`}
                  >
                    {/* Finder accent rail */}
                    {isFinder && (
                      <span
                        aria-hidden="true"
                        data-testid={`admin-row-finder-accent-${it.id}`}
                        className="absolute left-0 top-0 bottom-0 w-[3px] bg-white"
                      />
                    )}

                    <div className="flex-none mt-1">
                      {it.status === "new" ? (
                        <CircleDot className="w-4 h-4 text-white" strokeWidth={1.5} />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-zinc-600" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Finder qualified-lead banner */}
                      {isFinder && (
                        <div
                          data-testid={`admin-row-finder-badge-${it.id}`}
                          className="inline-flex items-center gap-2 mb-2 px-2.5 py-1 bg-white text-black text-[10px] font-semibold uppercase tracking-[0.18em]"
                        >
                          <Sparkles className="w-3 h-3" strokeWidth={2} />
                          Empfohlen vom Verfahren-Finder
                          <span className="opacity-60">· qualifiziert</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display text-base font-semibold truncate">
                          {it.name}
                        </span>
                        <span className="text-zinc-500 text-sm truncate">·  {it.email}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                        <span>{STATUS_LABEL[it.status] || it.status}</span>
                        {it.attachment_filename && (
                          <span className="inline-flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Anhang
                          </span>
                        )}
                        <span>{new Date(it.created_at).toLocaleString("de-CH")}</span>
                      </div>
                      <p className="text-sm text-zinc-300 line-clamp-2">{it.message}</p>
                      {(it.material || it.goal || it.service_interest) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {it.service_interest && (
                            <span
                              className={`inline-block text-[11px] px-2 py-0.5 ${
                                isFinder
                                  ? "bg-white/10 border border-white/30 text-white"
                                  : "border border-white/15 text-zinc-300"
                              }`}
                            >
                              → {it.service_interest}
                            </span>
                          )}
                          {it.material && (
                            <span className="inline-block text-[11px] px-2 py-0.5 border border-white/10 text-zinc-500">
                              {it.material}
                            </span>
                          )}
                          {it.goal && (
                            <span className="inline-block text-[11px] px-2 py-0.5 border border-white/10 text-zinc-500">
                              {it.goal}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-5 bg-[#0B0B0D]">
            {selected ? (
              <div className="p-8 md:p-10 sticky top-20" data-testid="admin-detail">
                {selected.source === "verfahren_finder" && (
                  <div
                    data-testid="admin-detail-finder-badge"
                    className="inline-flex items-center gap-2 mb-5 px-2.5 py-1 bg-white text-black text-[10px] font-semibold uppercase tracking-[0.18em]"
                  >
                    <Sparkles className="w-3 h-3" strokeWidth={2} />
                    Empfohlen vom Verfahren-Finder · qualifiziert
                  </div>
                )}
                <div className="overline mb-3">— Anfrage</div>
                <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                  {selected.name}
                </h2>
                <div className="text-zinc-400 mb-6">{selected.email}</div>

                <div className="space-y-3 mb-6 text-sm">
                  {selected.company && (
                    <div className="flex items-center gap-3 text-zinc-300">
                      <Building2 className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                      {selected.company}
                    </div>
                  )}
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone}`}
                      className="flex items-center gap-3 text-zinc-300 hover:text-white"
                    >
                      <Phone className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                      {selected.phone}
                    </a>
                  )}
                  <a
                    href={`mailto:${selected.email}`}
                    className="flex items-center gap-3 text-zinc-300 hover:text-white"
                  >
                    <Mail className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                    Antworten
                  </a>
                </div>

                {selected.source === "verfahren_finder" && (
                  <div className="mb-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                    {[
                      ["Empfehlung", selected.service_interest],
                      ["Material", selected.material],
                      ["Ziel", selected.goal],
                      ["Stückzahl", selected.quantity],
                      ["Termin", selected.timing],
                    ]
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <div key={k} className="bg-[#0B0B0D] p-3.5">
                          <div className="overline mb-1">{k}</div>
                          <div className="text-sm text-white">{v}</div>
                        </div>
                      ))}
                  </div>
                )}

                <div className="overline mb-2">Nachricht</div>
                <pre className="whitespace-pre-wrap font-body text-sm text-zinc-300 bg-[#121214] border border-white/10 p-4 mb-6">
                  {selected.message}
                </pre>

                {selected.attachment_filename && (
                  <button
                    type="button"
                    onClick={() => downloadAttachment(selected.id, selected.attachment_filename)}
                    data-testid="admin-download-attachment"
                    className="w-full inline-flex items-center justify-between gap-3 border border-white/15 hover:bg-white/5 px-4 py-3 mb-6 text-sm text-white"
                  >
                    <span className="flex items-center gap-2 truncate">
                      <FileText className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                      <span className="truncate">{selected.attachment_filename}</span>
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">
                      Download
                    </span>
                  </button>
                )}

                <div className="overline mb-2">Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      data-testid={`admin-status-${s}`}
                      className={`px-3 py-2 text-[12px] border transition-colors ${
                        selected.status === s
                          ? "bg-white text-black border-white"
                          : "border-white/15 text-zinc-300 hover:bg-white/5"
                      }`}
                    >
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-10 text-zinc-500 text-sm">
                Wählen Sie links eine Anfrage aus.
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
